import { assertIsoTimestamp, IsoTimestamp, formatToTimeZone, startOfDay } from '#datetime';
import { Box } from '@mui/material';
import { DateCalendar, dateCalendarClasses, dayPickerClasses } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, PickersDayProps, pickersDayClasses } from '@mui/x-date-pickers/PickersDay';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useCallback } from 'react';

export type Props = {
  /**
   * The current selected date of the time picker
   */
  date?: IsoTimestamp;

  defaultDate?: IsoTimestamp;

  minDate?: IsoTimestamp;

  maxDate?: IsoTimestamp;

  timezone: string;

  'data-testid'?: string;

  renderDay?: (props: PickersDayProps<IsoTimestamp>) => JSX.Element;

  onChange: (time: IsoTimestamp) => void;

  onMonthChange?: (month: IsoTimestamp) => void;

  disablePast?: boolean;

  disableFuture?: boolean;

  /**
   * If true, the time component of the date will be preserved when the date is changed.
   */
  shouldPreserveTime?: boolean;

  shouldDisableDate?: (day: IsoTimestamp) => boolean;
};

const toIsoTimestampDayProps = (dateProps: PickersDayProps<Date>, day: IsoTimestamp): PickersDayProps<IsoTimestamp> => {
  return Object.assign(Object.create(null), dateProps, { day });
};

const BaseDatePicker = ({
  date,
  defaultDate,
  minDate,
  maxDate,
  timezone,
  'data-testid': dataTestId,
  renderDay,
  onChange,
  onMonthChange,
  disablePast,
  disableFuture,
  shouldPreserveTime,
  shouldDisableDate,
}: Props) => {
  const renderDayWrapper = useCallback(
    (props: PickersDayProps<Date>) => {
      if (!renderDay) {
        return (
          <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center">
            <PickersDay
              {...props}
              // eslint-disable-next-line no-restricted-syntax
              sx={{
                ...props.sx,
                width: '40px',
                height: '40px',
              }}
            />
          </Box>
        );
      }

      const convertedDay = assertIsoTimestamp(fromZonedTime(props.day, timezone).toISOString());
      const isoTimestampProps = toIsoTimestampDayProps(props, convertedDay);
      return renderDay({
        ...isoTimestampProps,
        // Convert from local timezone to the timezone of the date picker
        // MUI's DateCalendar always uses the local timezone
        day: convertedDay,
      });
    },
    [renderDay, timezone]
  );

  // Converting the date passed to DateCalendar to local timezone so that the date picker can render the date correctly.
  // This is necessary because MUI's DateCalendar always uses the local timezone.
  // Any date returned in the onChange callback will be in the local timezone
  // and will need to be converted to the timezone passed to the DateCalendar.
  return (
    <DateCalendar
      data-testid={dataTestId}
      disableHighlightToday
      /**
       * The below 4 timezone conversions seem counterintuitive.
       * In the comment above, we say we want to pass the date in the local timezone,
       * but we are using toZonedTime. It seems like converting to the selected timezone.
       * However with toZonedTime we are saying is, give us what the date would look like in selected timezone.
       *
       * E.g:
       *  - Local timezone: Asia/Kolkata
       *  - if we pass in 2025-01-15T03:30:00.000Z and the selected timezone is America/New_York,
       *    - This is 14th Jan 2025 10:30 PM in America/New_York
       *    - But the same ISO timestamp is 15th Jan 2025 09:00 AM in Asia/Kolkata
       *    - Due to this <DateCalendar /> will show 15th as the selected date, instead of 14th
       *    - toZonedTime will give us 14th Jan 2025 10:30 PM date in Asia/Kolkata (local timezone),
       *      which is the correct date in America/New_York.
       *
       * This is why we use fromZonedTime to convert to the local timezone.
       *
       * Similarly when we get the date back from <DateCalendar /> as callbacks we use fromZonedTime
       * to convert to the correct ISO timestamp in the selected timezone.
       */
      value={date ? toZonedTime(date, timezone) : undefined}
      defaultValue={defaultDate ? toZonedTime(defaultDate, timezone) : undefined}
      minDate={minDate ? toZonedTime(minDate, timezone) : undefined}
      maxDate={maxDate ? toZonedTime(maxDate, timezone) : undefined}
      disablePast={disablePast}
      disableFuture={disableFuture}
      shouldDisableDate={
        shouldDisableDate
          ? (day: Date) => {
              return shouldDisableDate(assertIsoTimestamp(day.toISOString()));
            }
          : undefined
      }
      timezone={timezone}
      views={['day']}
      slots={{ day: renderDayWrapper }}
      dayOfWeekFormatter={(_, date) =>
        formatToTimeZone(assertIsoTimestamp(fromZonedTime(date, timezone).toISOString()), 'EEE', timezone)
      }
      onChange={(value: Date) => {
        const startOfDayTime = shouldPreserveTime
          ? assertIsoTimestamp(fromZonedTime(value, timezone).toISOString())
          : startOfDay(assertIsoTimestamp(fromZonedTime(value, timezone).toISOString()), timezone);
        onChange(startOfDayTime);
      }}
      onMonthChange={
        onMonthChange ? (month: Date) => onMonthChange(assertIsoTimestamp(month.toISOString())) : undefined
      }
      // eslint-disable-next-line no-restricted-syntax
      sx={{
        maxHeight: 'unset',
        height: '100%',
        width: '100%',
        minWidth: '280px',
        minHeight: '280px',

        [`& .${dateCalendarClasses.viewTransitionContainer}`]: {
          height: '100%',
          '& > div:first-of-type': {
            height: '100%',
          },
        },
        [`& .${dayPickerClasses.root}`]: {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
        [`& .${dayPickerClasses.weekDayLabel}`]: {
          width: '100%',
        },
        [`& .${dayPickerClasses.slideTransition}`]: {
          flexGrow: 1,
        },
        [`& .${pickersDayClasses.root}`]: {
          flexGrow: renderDay ? 1 : 0,
        },
      }}
    />
  );
};

export default BaseDatePicker;
